import { useMemo } from 'react';
import {
  chartConfigs, tradeMap, colorMap, fillMissingValues, toMap, aggregateYearly
} from '../utils/constants';

export const useDataProcessing = (chartType, data, range, filteredDates, tradeMode, jobMode) => {
  return useMemo(() => {
    if (!data.length || !chartType) {
      return {
        dualLineSeries: [],
        tradeSeries: [],
        debtSeries: [],
        chartData: null,
        transformed: []
      };
    }

    let dualLineSeries = [];
    let tradeSeries = [];
    let debtSeries = [];
    let chartData = null;
    const transformed = [];

    // 双轴图表数据处理
    if (chartConfigs[chartType]?.type === "dualLine") {
      let bondData, assetData;
      
      if (chartType === 'stockGold' || chartType === 'stockBitcoin') {
        bondData = data.filter(d => d.name === '道瓊指數');
        assetData = data.filter(d => (d.name === '黃金' || d.name === '比特幣'));
      } else {
        bondData = data.filter(d => (d.name === '10年期美債' || d.name === 'VIX恐慌指數'));
        assetData = data.filter(d => 
          ['WTI原油', '黃金', '比特幣', 'DXY美元指數', '道瓊指數'].includes(d.name)
        );
      }
      
      const bondValues = filteredDates.map(date => {
        const bondPoint = bondData.find(d => d.date === date);
        return bondPoint ? bondPoint.value : null;
      });
      
      const assetValues = filteredDates.map(date => {
        const assetPoint = assetData.find(d => d.date === date);
        return assetPoint ? assetPoint.value : null;
      });

      dualLineSeries = [
        { 
          data: fillMissingValues(bondValues), 
          label: chartConfigs[chartType].leftLabel,
          yAxisId: 'leftAxisId',
          color: '#08dfeeff',
          showMark: false
        },
        { 
          data: fillMissingValues(assetValues), 
          label: chartConfigs[chartType].rightLabel,
          yAxisId: 'rightAxisId',
          color: '#eea913ff',
          showMark: false
        }
      ];
    }
    
    // 数据处理 (usJobs)
    else if (chartType === "usJobs") {
      // 根據 jobMode 決定顯示哪些數據
      const isSmallJob = jobMode === "smallJob";
      
      if (isSmallJob) {
        // 小非農：只顯示 ADP，單軸
        const adpData = data.filter(d => d.name === "ADP私營就業人數變化");
        const adpMap = toMap(adpData);
        const adpValues = filteredDates.map(date => adpMap.get(date) ?? null);
        if (range === 365*100 + 31) {
          const employmentYearly = aggregateYearly(adpData, "sum");
          const employmentMap = new Map(employmentYearly.map(d => [d.year, d.value]));

          const years = Array.from(new Set([
            ...employmentYearly.map(d => d.year),
          ])).sort();

          chartData = years.map(year => ({
            year,
            employment: employmentMap.get(year) ?? 0,
          }));
        } else {
          dualLineSeries = [
            {
              data: fillMissingValues(adpValues),
              label: "ADP私營就業人數變化（人）",
              yAxisId: "leftAxisId",
              color: "#08dfeeff",
            },
          ];
        }
      } else {
        // 大非農（原本邏輯不變）
        const jobsData = data.filter(d => d.name === "非農就業人數");
        const unemploymentData = data.filter(d => d.name === "失業率");
      
        if (range === 365*100 + 31) {
          const employmentYearly = aggregateYearly(jobsData, "average");
          const unemploymentYearly = aggregateYearly(unemploymentData, "average");
          const employmentMap = new Map(employmentYearly.map(d => [d.year, d.value]));
          const unemploymentMap = new Map(unemploymentYearly.map(d => [d.year, d.value]));

          const years = Array.from(new Set([
            ...employmentYearly.map(d => d.year),
            ...unemploymentYearly.map(d => d.year),
          ])).sort();

          chartData = years.map(year => ({
            year,
            employment: employmentMap.get(year) ?? 0,
            unemployment: unemploymentMap.get(year) ?? 0,
          }));
        } else {
          const jobMap = toMap(jobsData);
          const jobsValues = filteredDates.map(date => jobMap.get(date) ?? null);
          const unJobMap = toMap(unemploymentData);
          const unJobsValues = filteredDates.map(date => unJobMap.get(date) ?? null);
          
          dualLineSeries = [
            {
              data: fillMissingValues(jobsValues),
              label: "非農就業人數（千人）",
              yAxisId: "leftAxisId",
              color: "#082beeff",
            },
            {
              data: fillMissingValues(unJobsValues),
              label: "失業率 (%)",
              yAxisId: "rightAxisId",
              color: "#ee1313ff",
            },
          ];
        }
      }
    }
    // 贸易数据处理
    else if (chartType === "trade" && data.length > 0) {
      if (tradeMode === "combined") {
        const importData = data.filter(d => d.name === "I_ALL");
        const exportData = data.filter(d => d.name === "E_ALL");
        const importMap = new Map(importData.map(d => [d.date, d.value]));
        const exportMap = new Map(exportData.map(d => [d.date, d.value]));
        
        tradeSeries = [
          { 
            data: filteredDates.map(date => importMap.get(date) || 0), 
            label: "進口總額", 
            color: "#b22222" 
          },
          { 
            data: filteredDates.map(date => exportMap.get(date) || 0), 
            label: "出口總額", 
            color: "#d2b48c" 
          },
        ];
      } else {
        const items = data.filter(d => 
          d.name.startsWith(tradeMode[0].toUpperCase() + "_") && /\d+$/.test(d.name)
        );
        const grouped = {};

        items.forEach(d => {
          const code = d.name.split("_")[1];
          const label = tradeMap[code] || code;
          if (!grouped[label]) grouped[label] = new Map();
          grouped[label].set(d.date, d.value);
        });

        tradeSeries = Object.keys(grouped).map(label => ({
          data: filteredDates.map(date => grouped[label].get(date) || 0),
          label,
          stack: tradeMode,
          color: colorMap[label] || "#888888",
        }));
      }
    }
    
    // 央行数据处理
    else if (chartType === "chinaPBOC") {
      try {
        const debtMap = {};
        
        if (Array.isArray(data)) {
          data.forEach((item, i) => {
            try {
              let date, name, value;
              
              if (Array.isArray(item) && item.length >= 3) {
                [date, name, value] = item;
              } else if (item && typeof item === 'object') {
                ({ date, name, value } = item);
              } else {
                return;
              }
              
              if (!date || !name || value === undefined || value === null) {
                return;
              }
              
              const key = typeof date === 'string' && date.length >= 7 ? 
                date.slice(0, 7) : date.toString().slice(0, 7);
              
              if (!debtMap[key]) debtMap[key] = {};
              debtMap[key][name] = parseFloat(value) || 0;
              
            } catch (itemError) {
              console.error(`Error processing item at index ${i}:`, itemError, item);
            }
          });
          
          const sortedDates = Object.keys(debtMap).sort();
          debtSeries = sortedDates.map(date => ({
            date,
            ...debtMap[date]
          }));
        }
      } catch (error) {
        console.error('Error in chinaPBOC data processing:', error);
      }
    }
    // 🔥 美国国债数据处理 (usDebt)
    else if (chartType === "usDebt") {
      console.log('[useDataProcessing] Processing usDebt');
      console.log('[useDataProcessing] First item:', data[0]);
      console.log('[useDataProcessing] All keys:', Object.keys(data[0] || {}));
      
      try {
        if (Array.isArray(data) && data.length > 0) {
          const firstItem = data[0];
          
          // 检测数据格式：是否是 name-value 格式
          const isNameValueFormat = firstItem.hasOwnProperty('name') && 
                                   firstItem.hasOwnProperty('value') ||
                                   firstItem.hasOwnProperty('metric_name') && 
                                   firstItem.hasOwnProperty('metric_value') ||
                                   firstItem.hasOwnProperty('metric_symbol') && 
                                   firstItem.hasOwnProperty('metric_value');
          
          console.log('[useDataProcessing] Is name-value format?', isNameValueFormat);
          
          if (isNameValueFormat) {
            // 格式1: name-value 格式，需要重组
            console.log('[useDataProcessing] Using name-value format processing');
            const debtMap = {};
            
            data.forEach(item => {
              const date = item.date || item.metric_date;
              const symbol = item.name || item.metric_name || item.metric_symbol;
              const value = item.value || item.metric_value;
              
              if (!date || !symbol || value === undefined || value === null) return;
              
              if (!debtMap[date]) {
                debtMap[date] = { date };
              }
              
              if (symbol === 'DEBT_INTRA' || symbol === '內部持有' || symbol === '内部持有') {
                debtMap[date].DEBT_INTRA = parseFloat(value) || 0;
              } else if (symbol === 'DEBT_PUB' || symbol === '公眾持有' || symbol === '公众持有') {
                debtMap[date].DEBT_PUB = parseFloat(value) || 0;
              } else if (symbol === 'DEBT_TOTAL' || symbol === '國債總額' || symbol === '国债总额') {
                debtMap[date].DEBT_TOTAL = parseFloat(value) || 0;
              }
            });
            
            const sortedDates = Object.keys(debtMap).sort();
            debtSeries = sortedDates.map(date => debtMap[date]);
            
            console.log('[useDataProcessing] Reorganized data sample:', debtSeries[0]);
          } else {
            // 格式2: 已经是对象格式，直接映射
            console.log('[useDataProcessing] Using direct object mapping');
            debtSeries = data
              .filter(item => item && typeof item === 'object' && item.date)
              .map(item => ({
                date: item.date,
                DEBT_INTRA: parseFloat(item.DEBT_INTRA || 0),
                DEBT_PUB: parseFloat(item.DEBT_PUB || 0),
              }));
          }
          
          console.log('[useDataProcessing] Processed usDebt series:', {
            count: debtSeries.length,
            first: debtSeries[0],
            last: debtSeries[debtSeries.length - 1],
            hasIntra: debtSeries.some(d => d.DEBT_INTRA > 0),
            hasPub: debtSeries.some(d => d.DEBT_PUB > 0),
          });
          
          // 如果还是都是0，输出警告
          if (!debtSeries.some(d => d.DEBT_INTRA > 0 || d.DEBT_PUB > 0)) {
            console.error('[useDataProcessing] ⚠️ All values are still 0! API may be returning wrong data.');
            console.error('[useDataProcessing] Raw data sample:', data.slice(0, 3));
          }
        }
      } catch (error) {
        console.error('Error in usDebt data processing:', error);
        debtSeries = [];
      }
    }    
    // 自由组合数据处理
    else if (chartType === "freedom") {
      const dateMap = {};
      
      data.forEach(({ date, name, value }) => {
        if (!dateMap[date]) dateMap[date] = { date };
        dateMap[date][name] = value;
      });
      
      const sortedDates = Object.keys(dateMap).sort();
      transformed.length = 0;
      sortedDates.forEach(key => {
        transformed.push(dateMap[key]);
      });
    }

    return {
      dualLineSeries,
      tradeSeries,
      debtSeries,
      chartData,
      transformed
    };
  }, [chartType, data, range, filteredDates, tradeMode, jobMode]); 
};